/**
 * Deep-links do Club Fidelidade: `?open=levels`, `?open=rewards`, `?open=loja` e
 * `?open=post&slug=…`.
 *
 * Existe um PADRÃO de URL no projeto (`?open=<destino>`), mas não um mecanismo:
 * hoje `story` é lido no `app.vue`, `cashback` no `useCashbackMenu` (com alias
 * `#cashback`) e `promocode` no `layouts/default`, cada um com sua cópia das
 * mesmas quatro etapas. Este registro é o mecanismo — por ora só para os
 * destinos do hub, que são novos. Os três antigos ficam onde estão até serem
 * migrados um a um, e por isso este leitor IGNORA os valores deles: dois donos
 * para o mesmo param abririam o painel duas vezes.
 *
 * As quatro etapas, num lugar só:
 *
 * 1. ler no mount E a cada navegação (SPA não remonta o `app.vue`);
 * 2. abrir SÓ o painel do destino, sem o hub atrás (ver `run`), e na hora quando
 *    há sessão — a espera da Smartico é problema do painel, que já tem o próprio
 *    loader. Segurar a abertura até a gamificação subir daria 3 segundos de
 *    "cliquei e não aconteceu nada";
 * 3. deslogado, guardar a intenção e abrir DEPOIS do login — senão o link de
 *    campanha larga o jogador na home;
 * 4. apagar o `open` da URL, senão F5 e o botão de voltar reabrem o painel
 *    sozinhos.
 */
import { storeToRefs } from 'pinia';
import { useLoyaltyHubStore } from '@/stores/loyalty-hub';

type HubDeeplink = 'levels' | 'rewards' | 'loja' | 'post';

/** Destinos que este leitor NÃO responde — cada um tem dono próprio hoje. */
const FOREIGN = new Set(['story', 'cashback', 'promocode']);

/** Destinos que abrem SEM sessão (ver o gate em `handle`). */
const PUBLIC = new Set<HubDeeplink>(['post']);

/** Intenção guardada enquanto o jogador passa pelo login. */
const pending = ref<{ target: HubDeeplink; slug?: string } | null>(null);

export function useLoyaltyHubDeeplink() {
  const route = useRoute();
  const router = useRouter();
  const store = useLoyaltyHubStore();
  const { isAuthenticated } = storeToRefs(useAuthStore());
  const { openLogin } = useAuth();

  function isHubTarget(value: unknown): value is HubDeeplink {
    return (
      value === 'levels' ||
      value === 'rewards' ||
      value === 'loja' ||
      value === 'post'
    );
  }

  /**
   * Abre SÓ o painel do destino — o hub NÃO sobe atrás.
   *
   * Quem chega por link quer aquela tela, não o clube: com o hub embaixo, o
   * jogador fecharia o painel e cairia num lugar onde não pediu para estar. Pelo
   * cartão dentro do clube o comportamento continua sendo o outro (o hub fica
   * atrás, e fechar volta pra ele) — quem decide é quem abre.
   *
   * Isso funciona porque o `<LoyaltyHub>` mora no layout e está sempre montado
   * (`layouts/default.vue`); o `isOpen` só governa o painel do hub, não os
   * sheets.
   */
  function run(target: HubDeeplink, slug?: string) {
    if (target === 'levels') store.openLevels();
    else if (target === 'rewards') store.openRewards();
    else if (target === 'loja') store.openStore();
    else if (target === 'post' && slug) store.openPost(slug);
  }

  /** Tira o `open` (e o `slug` que veio com ele) da URL, preservando o resto. */
  function cleanUrl() {
    const { open, slug, ...rest } = route.query as Record<string, unknown>;

    if (open === undefined) return;

    router.replace({ path: route.path, query: rest, hash: '' }).catch(() => {});
  }

  function handle(query: Record<string, unknown>) {
    const raw = Array.isArray(query.open) ? query.open[0] : query.open;

    if (typeof raw !== 'string' || FOREIGN.has(raw) || !isHubTarget(raw))
      return;

    const slugRaw = Array.isArray(query.slug) ? query.slug[0] : query.slug;
    const slug = typeof slugRaw === 'string' ? slugRaw : undefined;

    // Sem sessão: manda pro login e guarda o destino, que o watch abaixo
    // consome quando a sessão entra. (Antes quem mandava pro login era o
    // `store.open()` do hub; sem o hub no caminho, o login é chamado aqui.)
    //
    // `post` é a EXCEÇÃO: é conteúdo público do blog — a mesma promoção abre
    // deslogada em `/blog/<slug>`, e o handler do post não manda token. Pedir
    // login aqui fazia o link de campanha cair no modal de login em vez da
    // promoção (inclusive pra quem ESTÁ logado, quando o deep-link é lido antes
    // da sessão hidratar). Os outros destinos dependem da gamificação e seguem
    // no gate.
    if (!isAuthenticated.value && !PUBLIC.has(raw)) {
      pending.value = { target: raw, slug };
      openLogin();
      cleanUrl();

      return;
    }

    run(raw, slug);
    cleanUrl();
  }

  function start() {
    handle(route.query as Record<string, unknown>);

    router.afterEach((to) => handle(to.query as Record<string, unknown>));

    watch(isAuthenticated, (authed) => {
      if (!authed || !pending.value) return;

      const { target, slug } = pending.value;

      pending.value = null;
      run(target, slug);
    });
  }

  return { start };
}
